import { useCallback, useMemo } from "react";
import { BsData, UserBet } from "../models/ExchBetSlip";
import { Runners } from "../models/EventData";
import { turboCashout as turboCashoutAction } from "../store/exchBetSlip/exchBetSlipAction";
import { store } from "../store/store";

export type MarketType = "MATCH_ODDS" | "BOOKMAKER";

export interface CashoutMarket {
    marketId: string;
    marketName: string;
    runners: Runners[];
    status?: string;
    limits?: {
        minBetValue?: number;
        maxBetValue?: number;
    };
}

export interface RiskRow {
    runnerId: string;
    runnerName: string;
    userRisk: number;
}

export interface CashoutCalculations {
    openBetsMap: Map<string, UserBet[]>;
    exposureMaps: Record<string, RiskRow[]>;
}

export interface CashoutResponse {
    runnerId: string;
    runnerName: string;
    betType: "BACK" | "LAY";
    oddValue: number;
    stake: number;
    riskAfter: { [key: string]: number };
}

export const useCashoutLogic = (
    sportId: string,
    markets: any[],
    openBetsData: UserBet[],
    marketType: MarketType,
    turboCashoutCommission: number
) => {
    // Consolidated P/L calculation function
    const calculatePL = useCallback((bets: (UserBet | BsData)[], runnerId: string) => {
        let pl = 0;

        bets.forEach((bet) => {
            // Different odd value calculation based on market type
            const plVal = marketType === "MATCH_ODDS"
                ? bet.oddValue * bet.stakeAmount - bet.stakeAmount
                : (bet.oddValue / 100 + 1) * bet.stakeAmount - bet.stakeAmount;

            if (bet.betType === "BACK") {
                if (bet.runnerId === runnerId) {
                    pl += plVal; // if runner wins
                } else {
                    pl -= bet.stakeAmount; // if runner loses
                }
            } else if (bet.betType === "LAY") {
                if (bet.runnerId === runnerId) {
                    pl -= plVal; // lay liability
                } else {
                    pl += bet.stakeAmount; // lay profit
                }
            }
        });

        return pl;
    }, [marketType]);

    // Memoized calculations to avoid recalculation
    const memoizedCalculations = useMemo((): CashoutCalculations => {
        // Build openBetsMap once
        const openBetsMap = new Map<string, UserBet[]>();
        openBetsData.forEach((bet) => {
            const key = bet.marketId;
            if (!openBetsMap.has(key)) {
                openBetsMap.set(key, []);
            }
            openBetsMap.get(key)?.push(bet);
        });

        // Build exposure maps for all markets
        const exposureMaps: Record<string, RiskRow[]> = {};
        markets.forEach((market) => {
            const key = market.marketId;
            const bets = openBetsMap.get(key) || [];
            exposureMaps[key] = market.runners.map((runner: any) => ({
                runnerId: runner.runnerId,
                runnerName: runner.runnerName,
                userRisk: calculatePL(bets, runner.runnerId),
            }));
        });

        return { openBetsMap, exposureMaps };
    }, [openBetsData, markets, marketType]);

    const getOpenBetsPL = useCallback((
        marketId: string,
        runner: any
    ) => {
        // Find the market name from markets to build the correct key
        const market = markets.find(m => m.marketId === marketId);
        if (!market) return null;

        const key = marketId;
        const riskRows = memoizedCalculations.exposureMaps[key];
        
        if (!riskRows) return null;

        const riskRow = riskRows.find(r => r.runnerId === runner.runnerId);
        const pl = riskRow?.userRisk || 0;
        
        // Return null if PL is 0 to hide it in UI
        return pl === 0 ? null : pl;
    }, [memoizedCalculations.exposureMaps, markets]);

    const getTotalPL = useCallback((
        marketId: string,
        runner: any,
        bets: BsData[]
    ) => {
        const mBetslipBets = bets.filter(
            (b) => b.marketId === marketId && b.stakeAmount && b.stakeAmount > 0
        );

        if (mBetslipBets.length === 0) {
            return [null];
        }

        const openBetsPL = getOpenBetsPL(marketId, runner);
        const betslipPL = calculatePL(mBetslipBets, runner.runnerId);
        const totalPL = (openBetsPL || 0) + betslipPL;

        // Return null if total PL is 0 to hide it in UI
        return [totalPL === 0 ? null : totalPL];
    }, [calculatePL, getOpenBetsPL]);

    const getOpenBetsPLInArray = useCallback((
        marketId: string,
        runner: any
    ) => {
        return [getOpenBetsPL(marketId, runner)];
    }, [getOpenBetsPL]);

    // Optimized cashout functions using memoized data
    const isHaveCashOut = useCallback((market: CashoutMarket, disabledStatus: string[]) => {
        // const state = store.getState();
        // const isCashoutEnabled = state.exchangeSports.isCashoutEnabled;
        // if (!isCashoutEnabled) return false;

        if (market?.runners?.length !== 2) {
            return false;
        }
        if (disabledStatus.includes(market?.status?.toLowerCase() || '')) {
            return false;
        }

        // Use pre-calculated exposure map
        const key = market.marketId;
        const riskRows = memoizedCalculations.exposureMaps[key];

        if (!riskRows || riskRows.length < 2) {
            return false;
        }

        const [A, B] = market.runners;
        const riskMap = new Map(
            riskRows.map((r) => [r.runnerId, Number(r.userRisk) || 0])
          );
        const PA = Number(riskMap.get(A.runnerId) ?? 0);
        const PB = Number(riskMap.get(B.runnerId) ?? 0);

        const riskDifference = Math.abs(PA - PB);
        return riskDifference > 0.1;
    }, [memoizedCalculations.exposureMaps]);

    const calCashout = (market: CashoutMarket, exposureMap: Record<string, RiskRow[]>, marketType: MarketType): CashoutResponse | null => {
        const key = market.marketId;
        const riskRows = exposureMap[key] as RiskRow[];

        if (!riskRows || riskRows.length < 2) {
            return null;
        }

        const [A, B] = market.runners;
        const riskMap = new Map(
            riskRows.map((r) => [r.runnerId, Number(r.userRisk) || 0])
        );
        const PA = Number(riskMap.get(A.runnerId) ?? 0);
        const PB = Number(riskMap.get(B.runnerId) ?? 0);

        return getResponse({ market, PA, PB, marketType });
    };

    const convertOddsToDecimalOdds = (odds: number, marketType: MarketType) => {
        if (marketType === "MATCH_ODDS") {
            return odds;
        }
        return odds > 0 ? (odds + 100) / 100 : 0;
    };

    const getResponse = ({
        market,
        PA,
        PB,
        marketType,
    }: {
        market: CashoutMarket;
        PA: number;
        PB: number;
        marketType: MarketType;
    }): CashoutResponse | null => {
        const [A, B] = market.runners;

        // Early exit if already equal
        const epsilon = 0.01;
        const riskDiff = Math.abs(PA - PB);

        if (riskDiff <= epsilon) {
            return null;
        }

        // Get odds with proper conversion based on market type
        const bestBack = (r: Runners): number | null => {
            const backPrice = r?.backPrices[0]?.price;
            if (!backPrice || backPrice === 0) {
                return null;
            }
            
            let convertedPrice = convertOddsToDecimalOdds(Number(backPrice), marketType);
            
            const n = Number(convertedPrice);
            return Number.isFinite(n) ? n : null;
        };
        
        const bestLay = (r: Runners): number | null => {
            const layPrice = r?.layPrices[0]?.price;
            if (!layPrice || layPrice === 0) {
                return null;
            }
            
            let convertedPrice = convertOddsToDecimalOdds(Number(layPrice), marketType);
            
            const n = Number(convertedPrice);
            return Number.isFinite(n) ? n : null;
        };

        const oA_back = bestBack(A),
            oA_lay = bestLay(A);
        const oB_back = bestBack(B),
            oB_lay = bestLay(B);

        const minStake = Number(market?.limits?.minBetValue ?? 0);
        const maxStake = Number(
            market?.limits?.maxBetValue ?? Number.POSITIVE_INFINITY
        );

        // Simulate post-trade risk
        const simulate = (
            betType: 'BACK' | 'LAY',
            onRunner: Runners,
            s: number,
            o: number
        ): { [key: string]: number } => {
            let a = Number(PA),
                b = Number(PB);
            const onA = onRunner.runnerId === A.runnerId;

            if (betType === 'BACK') {
                const win = (o - 1) * s,
                    lose = -s;

                if (onA) {
                    a += win;
                    b += lose;
                } else {
                    b += win;
                    a += lose;
                }
            } else {
                const win = -(o - 1) * s,
                    lose = +s;

                if (onA) {
                    a += win;
                    b += lose;
                } else {
                    b += win;
                    a += lose;
                }
            }

            return { [A.runnerId]: +a.toFixed(2), [B.runnerId]: +b.toFixed(2) };
        };

        // Generate candidates
        const candsIdeal: Array<{
            betType: 'BACK' | 'LAY';
            runner: Runners;
            odds: number;
            stake: number;
            riskAfter?: { [key: string]: number };
        }> = [];

        const worseIsA = PA < PB;
        const pnlDiff = Math.abs(PB - PA);

        if (worseIsA) {
            if (oA_back !== null && Number.isFinite(oA_back)) {
                const s = pnlDiff / oA_back;
                if (s > 0)
                    candsIdeal.push({
                        betType: 'BACK',
                        runner: A,
                        odds: oA_back,
                        stake: s,
                    });
            }
            if (oB_lay !== null && Number.isFinite(oB_lay)) {
                const s = pnlDiff / oB_lay;
                if (s > 0)
                    candsIdeal.push({
                        betType: 'LAY',
                        runner: B,
                        odds: oB_lay,
                        stake: s,
                    });
            }
        } else {
            if (oB_back !== null && Number.isFinite(oB_back)) {
                const s = pnlDiff / oB_back;
                if (s > 0)
                    candsIdeal.push({
                        betType: 'BACK',
                        runner: B,
                        odds: oB_back,
                        stake: s,
                    });
            }
            if (oA_lay !== null && Number.isFinite(oA_lay)) {
                const s = pnlDiff / oA_lay;
                if (s > 0)
                    candsIdeal.push({
                        betType: 'LAY',
                        runner: A,
                        odds: oA_lay,
                        stake: s,
                    });
            }
        }

        if (candsIdeal.length === 0) {
            return null;
        }

        // Score and sort candidates
        const scoreWorst = (riskAfter: { [key: string]: number }) =>
            Math.min(...Object.values(riskAfter));

        candsIdeal.forEach((c) => {
            c.riskAfter = simulate(c.betType, c.runner, c.stake, c.odds);
        });

        candsIdeal.sort(
            (x, y) => scoreWorst(y.riskAfter!) - scoreWorst(x.riskAfter!)
        );

        const ideal = candsIdeal[0]
            ? {
                  runnerId: candsIdeal[0].runner.runnerId,
                  runnerName: candsIdeal[0].runner.runnerName,
                  betType: candsIdeal[0].betType,
                  oddValue: +candsIdeal[0].odds.toFixed(2),
                  stake: +candsIdeal[0].stake.toFixed(2),
                  riskAfter: candsIdeal[0].riskAfter!,
              }
            : null;

        // Apply constraints
        const applyConstraints = (
            cand: (typeof candsIdeal)[0]
        ): CashoutResponse | null => {
            if (!cand) {
                return null;
            }

            let s = Math.max(cand.stake, minStake);
            s = Math.min(s, maxStake);

            if (!(s > 0) || !Number.isFinite(s)) {
                return null;
            }

            return {
                runnerId: cand.runner.runnerId,
                runnerName: cand.runner.runnerName,
                betType: cand.betType,
                oddValue: +cand.odds.toFixed(2),
                stake: +s.toFixed(2),
                riskAfter: simulate(cand.betType, cand.runner, s, cand.odds),
            };
        };

        const constrained = applyConstraints(candsIdeal[0]);

        // Pick best option
        const pick = (a: CashoutResponse | null, b: CashoutResponse | null): CashoutResponse | null => {
            if (a && b) {
                const sa = scoreWorst(a.riskAfter),
                    sb = scoreWorst(b.riskAfter);
                return sa >= sb ? a : b;
            }
            return a ?? b ?? null;
        };

        return pick(constrained, ideal);
    };

    // Shared cashout calculation function with memoization
    const calculateCashout = useCallback((market: CashoutMarket): CashoutResponse | null => {
        const response = calCashout(market, memoizedCalculations.exposureMaps, marketType);

        return response;
    }, [memoizedCalculations.exposureMaps, marketType]);

    const getCashoutProfit = useCallback((market: CashoutMarket) => {
        // Proceed only for markets with 2 runners
        if (market.runners.length !== 2) return 0;

        const key = market.marketId;
        if (!memoizedCalculations.openBetsMap.has(key) ||
            memoizedCalculations.openBetsMap.get(key)?.length === 0) return 0;

        const response = calculateCashout(market);
        if (response == null || response?.oddValue == 0) return Infinity;
        return response?.riskAfter?.[market.runners[0].runnerId] || 0;
    }, [calculateCashout, memoizedCalculations.openBetsMap]);

    // Turbo Cashout Functions
    const getTurboCashoutData = useCallback((market: CashoutMarket, exposureMap: Record<string, RiskRow[]>) => {
        if (sportId !== "4") {
            return null;
        }
        const key = market.marketId;
        const riskRows = exposureMap[key] as RiskRow[];

        if (!riskRows || riskRows.length < 2) return null;

        const [A, B] = market.runners;
        const riskMap = new Map(
            riskRows.map((r) => [r.runnerId, Number(r.userRisk) || 0])
        );
        const PA = Number(riskMap.get(A.runnerId) ?? 0);
        const PB = Number(riskMap.get(B.runnerId) ?? 0);

        // Get the least risk value
        const leastRisk = Math.min(PA, PB);
        const turboCashoutAmount = leastRisk * (1 - turboCashoutCommission / 100);

        return {
            runnerA: { name: A.runnerName, risk: PA },
            runnerB: { name: B.runnerName, risk: PB },
            turboCashoutAmount: +turboCashoutAmount.toFixed(2),
        };
    }, [turboCashoutCommission]);

    const turboCashout = useCallback(async (
        market: CashoutMarket,
        sportId: string,
        eventId: string,
        provider: string,
        dispatch: Function
    ) => {
        if (sportId !== "4") {
            return false;
        }
        const state = store.getState();
        const turboCashoutCommission = state.exchangeSports.turboCashoutCommission;
        const isCashoutEnabled = state.exchangeSports.isCashoutEnabled;
        if (!isCashoutEnabled) return false;
        if (turboCashoutCommission <=0) return false;
        
        if (market == null) return;

        try {
            return await turboCashoutAction(market.marketId, sportId, eventId, provider, dispatch);
        } catch (error: any) {
            // Error is already handled in turboCashoutAction function
            throw error; // Re-throw to be caught by the button handler
        }
    }, []);

    const isTurboCashoutAvailable = useCallback((market: CashoutMarket, exposureMap: Record<string, RiskRow[]>) => {
        if (sportId !== "4") {
            return false;
        }
        const state = store.getState();
        const turboCashoutCommission = state.exchangeSports.turboCashoutCommission;
        // const isCashoutEnabled = state.exchangeSports.isCashoutEnabled;
        // if (!isCashoutEnabled) return false;
        if (turboCashoutCommission <=0) return false;
        
        const key = market.marketId;
        const riskRows = exposureMap[key] as RiskRow[];

        if (!riskRows || riskRows.length < 2) return false;

        const [A, B] = market.runners;
        const riskMap = new Map(
            riskRows.map((r) => [r.runnerId, Number(r.userRisk) || 0])
        );
        const PA = Number(riskMap.get(A.runnerId) ?? 0);
        const PB = Number(riskMap.get(B.runnerId) ?? 0);

        // Both runners should have positive P&L greater than 100
        // Risk difference should be less than 10
        const riskDifference = Math.abs(PA - PB);
        return PA >= 100 && PB >= 100 && riskDifference <= 10;
    }, [turboCashoutCommission]);

    return {
        getTotalPL,
        getOpenBetsPLInArray,
        isHaveCashOut,
        calculateCashout,
        getCashoutProfit,
        getTurboCashoutData,
        turboCashout,
        isTurboCashoutAvailable,
        exposureMaps: memoizedCalculations.exposureMaps as Record<string, RiskRow[]>,
    };
};
